import React, { memo, useMemo, useRef } from 'react';
import styled from 'styled-components';
import defaultImg from '../../asset/project/default_1.png';
import { WorkData } from '../../shared/dto/EntityRepository';
import { useWorkViewStore } from '../../shared/store/WorkViewStore';
import { useWorkPropertyVisible } from '../../shared/hooks/useApi/useWorkPropertyVisible';
import { useWorkContentModeStore } from '../../shared/store/workContentModeStore';
import { resolveWorkTitle } from '../../shared/util/workDisplayText';
import { useOptimizedImageUrl } from '../../shared/hooks/useOptimizedImageUrl';
import { useWorkIndexStore } from '../../shared/store/WorkIndexStore';
import { useWorkListMarqueeStore } from '../../shared/store/workListMarqueeStore';
import { useObjectUrl } from '../../shared/hooks/useObjectUrl';

export interface WorkInfoRowProps {
  data: WorkData;
  index: number;
  handleWorkClick: (data: WorkData, event: React.MouseEvent) => void;
}

const WorkInfo: React.FC<WorkInfoRowProps> = ({ data, index, handleWorkClick }) => {
  const workId = String(data.id);
  const activeWorkForRow = useWorkViewStore((s) =>
    s.activeWork && String(s.activeWork.id) === workId ? s.activeWork : null,
  );
  const pendingImageFile = useWorkViewStore((s) =>
    s.activeWork && String(s.activeWork.id) === workId ? s.pendingImage?.file ?? null : null,
  );
  const hasActiveWork = useWorkViewStore((s) => s.activeWork != null);
  const isInMulti = useWorkViewStore((s) =>
    s.multiSelectedWork.some((work) => String(work.id) === workId),
  );
  const isHitByMarquee = useWorkListMarqueeStore((s) =>
    s.hitWorks.some((work) => String(work.id) === workId),
  );
  const hasMarqueeHits = useWorkListMarqueeStore((s) => s.hitWorks.length > 0);
  const hasMulti = useWorkViewStore((s) => s.multiSelectedWork.length > 0);
  const { workPropertyVisible } = useWorkPropertyVisible();
  const workContentMode = useWorkContentModeStore((state) => state.workContentMode);
  const isIndexingMode = useWorkIndexStore((s) => s.isIndexingMode);
  const isInIndexing = useWorkIndexStore((s) =>
    s.selectedIndexingWorkList.some((work) => String(work.id) === workId),
  );
  const addSelectedIndexingWork = useWorkIndexStore((s) => s.addSelectedIndexingWork);
  const imgContainerRef = useRef<HTMLDivElement>(null);

  const isRowActive = activeWorkForRow != null;
  const isSelected = isRowActive || isInMulti || isHitByMarquee;
  const dimUnselected = isIndexingMode
    ? !(isInIndexing || isHitByMarquee)
    : (hasActiveWork || hasMulti || hasMarqueeHits) && !isSelected;
  const rowWork = activeWorkForRow ?? data;
  const effectiveOriginUrl = isRowActive
    ? activeWorkForRow.uploadFile?.originUrl ?? data.uploadFile?.originUrl ?? ''
    : data.uploadFile?.originUrl ?? '';
  const optimizedImageUrl = useOptimizedImageUrl(effectiveOriginUrl, imgContainerRef);
  const pendingImageUrl = useObjectUrl(pendingImageFile);
  const imageUrl = pendingImageUrl || optimizedImageUrl;
  const engVisible = workPropertyVisible?.engVisible !== false;

  const displayTitle = useMemo(
    () => resolveWorkTitle(rowWork, workContentMode, engVisible),
    [rowWork, workContentMode, engVisible],
  );

  const handleClick = (event: React.MouseEvent) => {
    if (isIndexingMode) addSelectedIndexingWork(data);
    else handleWorkClick(data, event);
  };

  if (!workPropertyVisible) return null;

  return (
    <Card
      data-work-id={data.id}
      onClick={handleClick}
      $isPublic={rowWork.isPublic !== false}
      $dimUnselected={dimUnselected}
      $isIndexingMode={isIndexingMode}
    >
      <IndexLabel>{index}.</IndexLabel>
      <ImageArea ref={imgContainerRef}>
        <WorkImage src={imageUrl || defaultImg} alt={displayTitle} draggable={false} />
      </ImageArea>
    </Card>
  );
};

export default memo(WorkInfo);

const Card = styled.article<{
  $isPublic: boolean;
  $dimUnselected: boolean;
  $isIndexingMode: boolean;
}>`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;
  position: relative;
  background: transparent;
  user-select: none;
  opacity: ${({ $isPublic, $dimUnselected, $isIndexingMode }) => {
    if ($dimUnselected) return 0.5;
    if ($isIndexingMode) return 1;
    return !$isPublic ? 0.6 : 1;
  }};
  cursor: pointer;
`;

const IndexLabel = styled.span`
  position: absolute;
  right: 100%;
  top: 0;
  padding-right: 6px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Footer_01};
  line-height: 1.2;
  user-select: none;
  pointer-events: none;
`;

const ImageArea = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
`;

const WorkImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: left top;
  -webkit-user-drag: none;
`;
